/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

// src/lib/msalInstance.ts
// MSAL (Microsoft Authentication Library) configuration for Azure AD / Entra ID

import { PublicClientApplication, LogLevel } from "@azure/msal-browser";

const clientId = (import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined) ?? "";
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID as string | undefined;
const frontEndUrl = import.meta.env.VITE_FRONT_END_URL as string | undefined;
const redirectUri = frontEndUrl ?? window.location.origin;

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";

// SECURITY: Prevent demo mode in production builds
if (import.meta.env.PROD && isDemoMode) {
  throw new Error(
    "SECURITY VIOLATION: Demo mode cannot be enabled in production builds. Please set VITE_DEMO_MODE=false."
  );
}

// Fail explicitly if Azure AD is not configured (unless demo mode)
if (!isDemoMode) {
  if (!clientId) {
    throw new Error(
      "VITE_AZURE_CLIENT_ID is required. Please configure Azure AD credentials in .env file."
    );
  }
  if (!tenantId) {
    throw new Error(
      "VITE_AZURE_TENANT_ID is required for secure authentication. The /common endpoint is not supported for single-tenant applications."
    );
  }
}

// Build authority URL
// Note: Do NOT append /v2.0 to the authority URL - MSAL handles this internally
// The authority should be: https://login.microsoftonline.com/{tenant_id}
const authorityEnv = import.meta.env.VITE_AZURE_AUTHORITY as string | undefined;
const authority =
  authorityEnv ??
  (tenantId
    ? `https://login.microsoftonline.com/${tenantId}`
    : "https://login.microsoftonline.com/common");

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: clientId,
    authority: authority,
    redirectUri: redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage", // Use sessionStorage for better security
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
    allowNativeBroker: false, // Disable native broker to avoid iframe issues
    iframeHashTimeout: 6000, // Reduce iframe timeout to fail faster and fall back to popup
  },
};

// Login request scopes
export const loginRequest = {
  scopes: ["User.Read", "openid", "profile", "email"],
};

// API scopes for backend calls
// IMPORTANT: This scope must match the exposed API in Azure Entra ID
// Go to Azure Portal > App Registrations > TORQE > Expose an API
// to create the "access_as_user" scope
export const apiScopes = {
  scopes: clientId ? [`api://${clientId}/access_as_user`] : [],
};

// Create the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance
  .initialize()
  .then(() => {
    // Handle redirect response
    msalInstance
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          msalInstance.setActiveAccount(response.account);
        }
      })
      .catch((error: unknown) => {
        const msalErr = error as {
          errorCode?: string;
          errorMessage?: string;
          subError?: string;
          stack?: string;
        };
        console.error("[MSAL] Redirect error:", {
          errorCode: msalErr.errorCode,
          errorMessage: msalErr.errorMessage,
          subError: msalErr.subError,
          stack: msalErr.stack,
        });
      });
  })
  .catch((error: unknown) => {
    const msalErr = error as { errorCode?: string; errorMessage?: string; stack?: string };
    console.error("[MSAL] Initialization error:", {
      errorCode: msalErr.errorCode,
      errorMessage: msalErr.errorMessage,
      stack: msalErr.stack,
    });
  });

export default msalInstance;
